// src/pages/epl/innovatech/components/steps/ContextoStep.jsx
import React, { useEffect, useState } from 'react';
import { Lightbulb, Target, FlaskConical, ArrowRight, Compass } from 'lucide-react';

/**
 * 🚀 Componente Contexto - Lab 1: Lean Startup Bootcamp
 * ✨ Rediseñado según EPL UX/UI Policy - Minimalista y profesional
 * 
 * @component
 * @description Página de bienvenida y contexto del laboratorio
 * @param {Function} onNext - Callback para avanzar al siguiente paso
 * @param {Object} labData - Datos del laboratorio (título, descripción, progreso)
 */
const ContextoStep = ({ onNext, labData }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isPulsing, setIsPulsing] = useState(false);

  // ✨ Microanimación de entrada
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    const pulseTimer = setTimeout(() => setIsPulsing(true), 800);
    const stopPulseTimer = setTimeout(() => setIsPulsing(false), 2300);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(pulseTimer);
      clearTimeout(stopPulseTimer);
    };
  }, []);

  return (
    <div className="space-y-6">
      {/* 📊 Información del laboratorio (fuera del header según UX/UI Policy) */}
      <div className={`transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}>
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            {labData?.title || 'Lean Startup Bootcamp'}
          </h1>
          <p className="text-sm text-gray-600 mb-3">
            {labData?.description || 'Mapear hipótesis central y circuito Build–Measure–Learn'}
          </p>
          
          {/* 📊 Progreso del laboratorio - Estilo EPL */}
          <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
            <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded-full font-medium">
              Paso {labData?.currentStep || 1} de {labData?.totalSteps || 8}
            </span>
            <span>•</span>
            <span>{labData?.progress || 13}% completado</span>
          </div>
        </div>
      </div>

      {/* 🎨 Bloque principal de contexto - Nuevo diseño EPL */}
      <div className={`transition-all duration-700 ease-out delay-200 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}>
        <div className="bg-green-50 sm:bg-green-50 bg-white border border-gray-200 sm:border-t-2 sm:border-t-green-600 border-t-0 rounded-xl p-4 sm:p-6 shadow-sm">
          {/* 🎯 Header minimalista */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Compass className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Bienvenido a InnovaTech
              </h3>
              <p className="text-sm text-gray-600">
                Domina el método Lean Startup paso a paso
              </p>
            </div>
          </div>
          
          {/* 📝 Objetivos de aprendizaje */}
          <div className="space-y-4 mb-8">
            <p className="text-gray-700 font-medium">
              En este laboratorio aprenderás a:
            </p>
            
            {/* 📋 Tarjetas de objetivos - Diseño minimalista */}
            <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-4">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Target className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900">Formular hipótesis</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Crear hipótesis claras y validables con formato estructurado
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <FlaskConical className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900">Diseñar experimentos</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Construir tu circuito Build-Measure-Learn completo
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* 🚀 CTA principal - Diseño EPL con microanimaciones */}
          <div className="flex justify-center">
            <button 
              onClick={onNext}
              className={`
                w-full sm:w-auto py-3 px-6 
                bg-green-600 hover:bg-green-800 text-white 
                rounded-lg font-semibold
                flex items-center justify-center gap-2
                transform hover:scale-105
                transition-all duration-200 ease-in-out
                ${isPulsing ? 'animate-pulse' : ''}
              `}
            >
              <Lightbulb className="w-5 h-5 mr-2" />
              Comenzar con mi hipótesis
            </button>
          </div>
        </div>
      </div>
      
      {/* 📱 Barra de progreso móvil - Sticky en móvil */}
      <div className="sm:hidden fixed top-0 left-0 right-0 z-40">
        <div className="h-0.5 bg-gray-200">
          <div 
            className="h-full bg-blue-500 transition-all duration-300" 
            style={{ width: `${labData?.progress || 13}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default ContextoStep;